import { PAYMENT_GATEWAYS } from "constants/paymentGateways";
import { CLIENT_URLS } from "constants/urls";
import { CARD_TYPES, RAZORPAY_CALLBACK_URL } from "layouts/Payment/common/dependencies/constants";
import { PAYMENT_FLOWS } from "constants/payment";
import { logger } from "logging";
import { localStorageService } from "service/storage";
import { isServer } from "helpers/utils/isServer";


interface RequestDTOType {
  payUPaymentRequestDTO: object,
  paymentExtraParamsDTO: object,
  razorPayPaymentRequestDTO: object,
  cashfreeSeamlessPaymentRequestDTO: object,
  paytmRequestDTO: object,
  orderData: {
    orderId: string,
  },
  shippingAddress: {
    email: string,
    telephone: string,
  }
}

// flow -- flow is used here to set lastRazorOrderId to localStorage on the basis of flow

export const buildRequestParams = (paymentGateway:string, requestDTO:RequestDTOType, razorPayCustId?:string, values?:object, shippingAddress?: object, flow = PAYMENT_FLOWS.CHECKOUT) => {
  const {
      payUPaymentRequestDTO,
      paymentExtraParamsDTO,
      razorPayPaymentRequestDTO,
      cashfreeSeamlessPaymentRequestDTO,
      paytmRequestDTO,
    } = requestDTO;

    let transformedDTO = {};
    let orderData = null;
    let shippingAddressModel = null;
    let orderId = "";
    let origin = null;

    switch (paymentGateway) {
      case PAYMENT_GATEWAYS.PAYU_UPI_INTENT:
        transformedDTO = {
          ...paymentExtraParamsDTO,
        };
        break;
      case PAYMENT_GATEWAYS.PAYU:
      case PAYMENT_GATEWAYS.PAYU_MARKET_PLACE:
        // received in saved payu cards for marketplace products
        transformedDTO = {
          ...payUPaymentRequestDTO,
          ...values,
        };
        break;
      case PAYMENT_GATEWAYS.RAZOR_PAY:
        orderData = requestDTO.orderData;
        shippingAddressModel = requestDTO.shippingAddress;
        orderId = orderData.orderId;

        if (!shippingAddressModel) {
          // shipping address missing, throw an exception here.
          logger.error(new Error("ShippingAddress cannot be null"));
          return;
        }
        // lastRazorOrderId need to be used in postPayment for wallet flow , so need to be set here
        if (flow === PAYMENT_FLOWS.WALLET) {
          const { razorpayOrderId } = razorPayPaymentRequestDTO;
          localStorageService.setItem("lastRazorOrderId", razorpayOrderId);
        }

        if (!isServer()) {
          origin = window.location.origin;
        }

        // eslint-disable-next-line no-case-declarations
        const { email, telephone } = shippingAddressModel;
        transformedDTO = {
          ...razorPayPaymentRequestDTO,
          ...values,
          ...{
            customer_id: razorPayCustId,
            email,
            telephone,
            nykOrderId: orderId,
            callback_url: `${origin}${RAZORPAY_CALLBACK_URL}`, // callback url to be passed
          },
        };
        break;

      case PAYMENT_GATEWAYS.RAZORPAY_GPAY_INTENT:
        if (!isServer()) {
          origin = window.location.origin;
        }
        orderData = requestDTO.orderData;
        orderId = orderData.orderId;
        transformedDTO = {
          ...razorPayPaymentRequestDTO,
          ...shippingAddress,
          ...values,
          ...{
            customer_id: razorPayCustId,
            nykOrderId: orderId,
            callback_url: `${origin}${CLIENT_URLS.POST_PAYMENT_PAGE}?ptype=razorpaySuccess&pgInternal=razorpay`, // callback url to be passed
          },
        };
        break;

      case PAYMENT_GATEWAYS.PAYTM:
        transformedDTO = {
          ...paytmRequestDTO,
          ...values,
        };
      break;
      // payment gateway other than that will be cashFree Seamless
      default:
        transformedDTO = {
          ...cashfreeSeamlessPaymentRequestDTO,
          ...values,
        };
        break;
      }

    return transformedDTO;
};

/**
 * @description
 * function that returns offers available for passed payment methods
 * @param availablePaymentMethods - payment Methods available
 * @param paymentMethodRule - payment method rule containing offers
 */
export const getAvailableOffers = (availablePaymentMethods: [string], paymentMethodRule: []) => {
  const availableOfferRules = availablePaymentMethods && paymentMethodRule && paymentMethodRule.filter((paymentRule: { paymentMethod: string}) => {
    let { paymentMethod } = paymentRule;
    return (availablePaymentMethods.includes(paymentMethod));
  });
  return availableOfferRules;
};

/**
 * mapping of card types is handled in this function
 * @param type - type of card from backend
 * @returns type that is used in our application which is further used to fetch images etc
 */
export const getCardTypeBasedOnType = (type: string) => {
  switch (type) {
    case "VISA":
      return CARD_TYPES.VISA;
    case "MASTERCARD":
    case "MAST":
      return CARD_TYPES.MASTERCARD;
    case "DINERSCLUB":
    case "DINERS CLUB":
    case "DINR":
      return CARD_TYPES.DINERS;
    case "AMERICAN EXPRESS":
    case "AMERICANEXPRESS":
    case "AMEX":
      return CARD_TYPES.AMEX;
    case "MAESTRO":
    case "MAES":
      return CARD_TYPES.MAESTRO;
    case "RUPAY":
      return CARD_TYPES.RUPAY;
    case "DISCOVER":
      return CARD_TYPES.DISCOVER;
    case "SBIMAESTRO":
      return CARD_TYPES.SBIMAESTRO;
    case "JCB":
      return CARD_TYPES.JCB;
    case "LASER":
      return CARD_TYPES.LASER;
    case "ENROUTE":
      return CARD_TYPES.ENROUTE;
    default:
      return CARD_TYPES.INVALID;
  }
};
